import moment from 'moment-timezone';

import { Component, forwardRef, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { reportSavedSourceTypes } from '../SeriesPickerField/series-picker-field.component';
import { ChartTimeIntervalsEnum } from '../../enums/chart-time-intervals.enum';
import { DropdownFieldComponent } from '../../../PageBuilder/components/FormFields/DropdownField/dropdown-field.component';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../../api/mentions/enum/relative-timezone-date-type.enum';


@Component({
    selector: 'agpr-time-interval-dropdown-field',
    templateUrl: '../../../PageBuilder/components/FormFields/DropdownField/dropdown-field.component.html',
    styleUrls: ['../../../PageBuilder/components/FormFields/DropdownField/dropdown-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => TimeIntervalDropdownFieldComponent)
        }
    ],
    standalone: false
})
export class TimeIntervalDropdownFieldComponent extends DropdownFieldComponent implements OnChanges {

    readonly disabledTimeIntervalsForRelativeDateRangeType = [ ChartTimeIntervalsEnum.MONTH, ChartTimeIntervalsEnum.QUARTER, ChartTimeIntervalsEnum.YEAR ];
    readonly hoursToTimeUnitsMap: Map<number, ChartTimeIntervalsEnum> = new Map<number, ChartTimeIntervalsEnum>([
        [ 8760, ChartTimeIntervalsEnum.YEAR ],
        [ 2160, ChartTimeIntervalsEnum.QUARTER ],
        [ 720, ChartTimeIntervalsEnum.MONTH ],
        [ 168, ChartTimeIntervalsEnum.WEEK ]
    ]);
    // these units in most time does not satisfy the same level of granularity, e.g. last week cannot have a time interval of one week, so use a smaller value
    readonly relativeTimezoneTypesToHoursMap: Map<RelativeTimezoneDateTypeEnum, number> = new Map<RelativeTimezoneDateTypeEnum, number>([
        [ RelativeTimezoneDateTypeEnum.TODAY, 12 ],
        [ RelativeTimezoneDateTypeEnum.YESTERDAY, 12 ],
        [ RelativeTimezoneDateTypeEnum.THIS_WEEK, 84 ],
        [ RelativeTimezoneDateTypeEnum.LAST_WEEK, 84 ],
        [ RelativeTimezoneDateTypeEnum.THIS_MONTH, 360 ],
        [ RelativeTimezoneDateTypeEnum.LAST_MONTH, 360 ],
        [ RelativeTimezoneDateTypeEnum.LAST_THREE_MONTHS, 1080 ],
        [ RelativeTimezoneDateTypeEnum.LAST_FOUR_MONTHS, 1440 ],
        [ RelativeTimezoneDateTypeEnum.LAST_24_HOURS, 24 ],
        [ RelativeTimezoneDateTypeEnum.LAST_3_DAYS, 72 ],
        [ RelativeTimezoneDateTypeEnum.LAST_7_DAYS, 84 ],
        [ RelativeTimezoneDateTypeEnum.LAST_30_DAYS, 360 ],
        [ RelativeTimezoneDateTypeEnum.LAST_90_DAYS, 1080 ],
    ]);
    readonly timeIntervalSuffixRegex = /\sby\s(weeks|months|quarters|years)$/i;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.value && !this.selectedOption) {
            this.setSelectedOption();
            return;
        }
        const series = (this.block.displayElement as ChartDisplayElementModel).series;
        this.options = this.field.options.slice();

        const areAllSavedSources = series.every(x => reportSavedSourceTypes.includes(x.type));
        if (!areAllSavedSources) {
            this.removeTimeIntervalByType(ChartTimeIntervalsEnum.YEAR);
        }

        const dateRange = series[0]?.dateRange;
        let durationHours = 8761; // by default allow all options
        if (dateRange) {
            switch (dateRange.type) {
                case DateQueryTypeEnum.RELATIVE:
                    durationHours = Math.abs(dateRange.relativeStartOffsetH);
                    break;
                case DateQueryTypeEnum.ABSOLUTE:
                    if (dateRange.absoluteDate) {
                        durationHours = 24;
                    } else if (dateRange.absoluteRange) {
                        durationHours = Math.abs(moment(dateRange.absoluteRange.end).diff(dateRange.absoluteRange.start, 'hours'));
                    }
                    break;
                case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                    durationHours = this.relativeTimezoneTypesToHoursMap.get(dateRange.relativeTimezoneDateType);
                    break;
            }
        }

        this.hoursToTimeUnitsMap.forEach((value, hour)=> {
            if (durationHours <= hour) {
                this.removeTimeIntervalByType(value as ChartTimeIntervalsEnum);
            }
        });

        if (this.selectedOption && this.options.findIndex(x => x.value === this.selectedOption.value) === -1) {
            this.selectedOption = this.options[0];
            this.onDropdownChanged(this.selectedOption.value);
        }
    }

    override onDropdownChanged(value: any) {
        this.value = value;
        this.changes.emit(this.value);
    }

    private removeTimeIntervalByType(interval: ChartTimeIntervalsEnum): void {
        const optionIndex = this.options.findIndex(x => x.value === interval);
        if (optionIndex > -1) {
            this.options.splice(optionIndex, 1);
        }
    }
}
